import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle, Loader2 } from "lucide-react";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";

const SUBMIT_USER_REPORT = graphql(`
  mutation SubmitUserReport($reportedUserId: ID!, $reason: String!, $description: String) {
    submitUserReport(input: { reportedUserId: $reportedUserId, reason: $reason, description: $description }) {
      report { id }
      errors
    }
  }
`);

// value must match UserReport::REASONS on the backend.
const REASONS: { value: string; labelKey: string }[] = [
  // #1157: first, because it is the one that cannot wait. Picking it shows the emergency line.
  { value: "safety", labelKey: "reportUserDialog.reasonSafety" },
  { value: "inappropriate_behavior", labelKey: "reportUserDialog.reasonInappropriateBehavior" },
  { value: "harassment", labelKey: "reportUserDialog.reasonHarassment" },
  { value: "fake_profile", labelKey: "reportUserDialog.reasonFakeProfile" },
  { value: "scam", labelKey: "reportUserDialog.reasonScam" },
  { value: "spam", labelKey: "reportUserDialog.reasonSpam" },
  { value: "other", labelKey: "reportUserDialog.reasonOther" },
];

const MAX_DETAILS = 5000;

interface ReportUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportedUserId: string;
  userName: string;
  context?: "profile" | "messaging";
}

const ReportUserDialog = ({ open, onOpenChange, reportedUserId, userName }: ReportUserDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await graphqlClient.request(
        SUBMIT_USER_REPORT,
        { reportedUserId, reason, description: details.trim() || null }
      );
      if (res.submitUserReport.errors.length > 0) throw new Error(res.submitUserReport.errors.join(", "));
    },
    onSuccess: () => setSubmitted(true),
    onError: (e: Error) => toast.error(e.message || t("reportUserDialog.submitError")),
  });

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setReason("");
      setDetails("");
      setSubmitted(false);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("reportUserDialog.title", { userName })}</DialogTitle>
              <DialogDescription>
                {t("reportUserDialog.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>{t("reportUserDialog.reasonLabel")}</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger><SelectValue placeholder={t("reportUserDialog.reasonPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {REASONS.map(r => <SelectItem key={r.value} value={r.value}>{t(r.labelKey)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {reason === "safety" && (
                <p role="note" data-testid="report-safety-hint" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground">
                  {t("reportUserDialog.safetyHint")}
                </p>
              )}
              <div className="space-y-2">
                <Label>{t("reportUserDialog.detailsLabel")}</Label>
                <Textarea
                  placeholder={t("reportUserDialog.detailsPlaceholder")}
                  value={details}
                  maxLength={MAX_DETAILS}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={3}
                />
              </div>
              <Button className="w-full" disabled={!reason || mutation.isPending} onClick={() => mutation.mutate()}>
                {mutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("reportUserDialog.submitting")}</> : t("reportUserDialog.submit")}
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-foreground mb-1">{t("reportUserDialog.successTitle")}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t("reportUserDialog.successBody")}</p>
            <Button onClick={handleClose}>{t("reportUserDialog.close")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportUserDialog;
